
import React from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardIcon } from "@heroicons/react/24/outline"; 
import { FiChevronRight } from "react-icons/fi";
import agent1Img from "../../assets/Agent2.png"
import agent2Img from "../../assets/Agent3.png"
import agent4Img from "../../assets/Agent6.png"

const CodeBlock = ({ code }) => {
  const [copied, setCopied] = React.useState(false);
  
  const copyToClipboard = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="relative bg-black/90 rounded-lg overflow-hidden mb-4">
      <div className="absolute top-2 right-2 z-10">
        <button
          onClick={copyToClipboard}
          className="p-1.5 rounded text-white"
          title="Copy to clipboard"
        >
          {copied ? (
            <span className="text-green-400 text-lg leading-none">✔</span>
          ) : (
            <ClipboardIcon className="w-4 h-4" />
          )}
        </button>
      </div>

      <pre className="text-xs text-green-400 p-4 pt-8 font-mono overflow-x-auto">
        {code}
      </pre>
    </div>
  );
};

export function renderPythonAgent() {
  return <PythonComponent />;
}

function PythonComponent() {
  const navigate = useNavigate();
    return (
    <div>
    <main className="transition-opacity duration-300">
    <div className="py-10">
    
    {/* Breadcrumbs */}
    <nav className="flex flex-wrap items-center gap-2 text-sm text-[#9a734c] mb-8">
          <a className="hover:text-primary hover:underline" href="#">Netraa APM</a>
          <FiChevronRight />
          <span>Agent Setup Guide</span>
          <FiChevronRight />
          <span>Technology Agent</span>
          <FiChevronRight />
          <span className="text-[#1b140d] dark:text-white font-medium">Python</span>
        </nav>
    
    {/* Intro */}
    <div className="mb-10 scroll-mt-24" id="introduction">
    <span className="inline-block px-3 py-1 mb-4 text-xs font-bold tracking-wider text-orange-600 uppercase bg-orange-100 rounded-full dark:bg-orange-900/30 dark:text-orange-400">
    Documentation
    </span>
    
    <h1 className="text-3xl lg:text-4xl font-extrabold text-text-main dark:text-white tracking-tight mb-4">
    <span className="bg-clip-text text-transparent bg-gradient-to-r from-orange-500 to-red-600">
    Netraa APM
    </span>{" "}
    Python Agent
    </h1>
    
    <p className="text-lg text-text-muted dark:text-gray-400 leading-relaxed">
    Netraa APM Python Agent enables end-to-end observability for Python
    applications by providing real-time tracing, performance metrics,
    error monitoring, and service topology visibility. It supports both
    monolithic and microservice-based Python applications.
    </p>
    </div>
    
    {/* Prerequisites */}
    <section className="mb-12 scroll-mt-24" id="prerequisites">
    <h2 className="text-2xl font-bold text-text-main dark:text-white mb-6">
    Prerequisites
    </h2>
    
    <div className="overflow-x-auto border border-border-color dark:border-border-dark rounded-xl">
    <table className="w-full text-sm">
    <thead className="bg-gray-100 dark:bg-white/5 text-left">
    <tr>
    <th className="px-4 py-3">Component</th>
    <th className="px-4 py-3">Requirement</th>
    </tr>
    </thead>
    <tbody>
    <tr className="border-t">
    <td className="px-4 py-3">Python Version</td>
    <td className="px-4 py-3">Python 3.8 or higher</td>
    </tr>
    
    <tr className="border-t">
    <td className="px-4 py-3">Network Access</td>
    <td className="px-4 py-3">Connectivity to Netraa Backend Collector</td>
    </tr>
    
    <tr className="border-t">
    <td className="px-4 py-3">Supported Frameworks</td>
    <td className="px-4 py-3">Django, Flask, General Python Applications</td>
    </tr>
    </tbody>
    </table>
    </div>
    </section>
    {/* Step 1 */}
    <section className="mb-12 scroll-mt-24" id="installation">
      <h1 className="text-2xl font-bold text-text-main dark:text-white mb-4">Steps to Follow:-</h1>
    <h2 className="text-2xl text-text-main dark:text-white mb-4">
    Step 1 — Install the Netraa Python Agent
    </h2>
    
    <p className="text-sm text-text-muted mb-3">
    Download the agents file from the ApMoSys share point and unzip it.
    </p>
    
    <CodeBlock code={`unzip Netraa-Python-Agent`} />
    
    <p className="text-sm text-text-muted mt-4 mb-2">
    After extracting the package you will see the following structure:
    </p>
    
    <CodeBlock code={`Netraa-Python-Agent/
     ├─ netraa_apm_python_agent-1.0.0.tar.gz
     ├─ netraa_apm_python_agent-1.0.0-py3-none-any.whl
     └─ README.md`} />
    
    <p className="text-sm text-text-muted mt-4">
    Navigate to your Python application directory:
    </p>
    
    <CodeBlock code={`cd /path/to/your/python/application`} />
    
    <div className="bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500 p-4 mt-6 rounded-r-lg">
    <p className="text-xs">
    <strong>Important:</strong> Ensure you are working inside the application's virtual environment before installing.
    </p>
    </div>
    
    <h3 className="font-bold text-lg mt-6 mb-2">Installation Options</h3>
    
    <p className="text-sm mb-2 font-semibold">Option A (Recommended): Install via Source Tarball</p>
    
    <CodeBlock code={`pip install /path/to/unzipped-folder/netraa_apm_python_agent-1.0.0.tar.gz`} />
    
    <p className="text-sm mt-4 mb-2 font-semibold">Option B: Install via Wheel</p>
    
    <CodeBlock code={`pip install /path/to/unzipped-folder/netraa_apm_python_agent-1.0.0-py3-none-any.whl`} />
    
    <p className="text-sm mt-4 mb-2 font-semibold">Example</p>
    
    <CodeBlock code={`pip install ~/Desktop/netraa_apm/netraa_apm_python_agent-1.0.0-py3-none-any.whl`} />
    
    </section>
    
    {/* Step 2 */}
    <section className="mb-12">
    <h2 className="text-2xl text-text-main dark:text-white mb-4">
    Step 2 — Verify Agent Installation
    </h2>
    
    <p className="text-sm mb-3">
    To confirm successful installation run:
    </p>
    
    <CodeBlock code={`pip list | grep netraa`} />
    
    <p className="text-sm mb-2">Expected Output</p>
    
    <CodeBlock code={`netraa_apm_python_agent    1.0.0`} />
    
    <p className="text-sm mt-4 mb-2">
    For detailed package information:
    </p>
    
    <CodeBlock code={`pip show netraa_apm_python_agent`} />
    
    </section>
    
    {/* Step 3 */}
    <section className="mb-12">
    <h2 className="text-2xl text-text-main dark:text-white mb-4">
    Step 3 — Framework-Specific Installation
    </h2>
    
    <p className="text-sm mb-3">For Django Applications</p>
    
    <CodeBlock code={`pip install "netraa_apm_python_agent[django]"`} />
    
    <p className="text-sm mt-4 mb-3">For Flask Applications</p>
    
    <CodeBlock code={`pip install "netraa_apm_python_agent[flask]"`} />
    
    <p className="text-sm text-text-muted">
    For general Python applications no additional framework installation is required.
    </p>
    </section>
    
    {/* Step 4 */}
    <section className="mb-12">
    <h2 className="text-2xl text-text-main dark:text-white mb-4">
    Step 4 — Configure Netraa Agent
    </h2>
    
    <p className="text-sm mb-4">
    Set the required environment variables before starting the application.
    </p>
    
    <CodeBlock code={`export NETRAA_AGENT_COLLECTOR_BACKEND_SERVICES=192.168.12.94:11800
    export NETRAA_AGENT_PROTOCOL=grpc
    export NETRAA_AGENT_NAME=my-service`} />
    
    <h3 className="font-bold text-lg mt-6 mb-4">Environment Variables</h3>
    
    <div className="overflow-x-auto border rounded-xl">
    <table className="w-full text-sm">
    <thead className="bg-gray-100 dark:bg-white/5 text-left">
    <tr>
    <th className="px-4 py-3">Variable</th>
    <th className="px-4 py-3">Description</th>
    </tr>
    </thead>
    
    <tbody>
    
    <tr className="border-t">
    <td className="px-4 py-3 font-mono">NETRAA_AGENT_COLLECTOR_BACKEND_SERVICES</td>
    <td className="px-4 py-3">Netraa backend collector IP and port</td>
    </tr>
    
    <tr className="border-t">
    <td className="px-4 py-3 font-mono">NETRAA_AGENT_PROTOCOL</td>
    <td className="px-4 py-3">Communication protocol (grpc)</td>
    </tr>
    
    <tr className="border-t">
    <td className="px-4 py-3 font-mono">NETRAA_AGENT_NAME</td>
    <td className="px-4 py-3">Logical service name displayed in Netraa UI</td>
    </tr>
    
    </tbody>
    </table>
    </div>
    
    <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-4 mt-6 rounded-r-lg">
    <p className="text-xs">
    Ensure <strong>NETRAA_AGENT_NAME</strong> is unique for each service in microservice environments.
    </p>
    </div>
    
    </section>
    
    {/* Step 5 */}
    <section className="mb-12">
    <h2 className="text-2xl text-text-main dark:text-white mb-4">
    Step 5 — Run Application with Netraa Agent
    </h2>
    
    <p className="text-sm mb-2">Django Application</p>
    
    <CodeBlock code={`netraa-python run python3 manage.py runserver`} />
    
    <p className="text-sm mt-4 mb-2">Flask Application</p>
    
    <CodeBlock code={`netraa-python run python3 app.py`} />
    
    <div className="bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500 p-4 mt-6 rounded-r-lg">
    <p className="text-xs">
    Before every application start ensure the <strong>NETRAA_AGENT_NAME</strong> environment variable is exported.
    </p>
    </div>
    
    </section>
    
    {/* Step 6 */}
    <section className="mb-12">
    <h2 className="text-2xl text-text-main dark:text-white mb-4">
    Step 6 — Uninstall the Agent
    </h2>
    
    <CodeBlock code={`pip uninstall -y netraa_apm_python_agent`} />
    
    </section>
    
    {/* Microservices */}
    <section className="mb-12 scroll-mt-24" id="microservices">
    <h2 className="text-2xl font-bold text-text-main dark:text-white mb-4">
    Running Netraa in a Microservices Architecture
    </h2>
    
    <p className="text-sm mb-4">
    Each microservice must run with a unique service name and port.
    </p>
    
    <p className="font-semibold text-sm mb-2">Microservice A</p>
    
    <CodeBlock code={`export NETRAA_AGENT_COLLECTOR_BACKEND_SERVICES=192.168.12.94:11800
    export NETRAA_AGENT_PROTOCOL=grpc
    export NETRAA_AGENT_NAME=py_a
    netraa-python run python3 service_a.py --port 5001`} />
    
    <p className="font-semibold text-sm mt-4 mb-2">Microservice B</p>
    
    <CodeBlock code={`export NETRAA_AGENT_COLLECTOR_BACKEND_SERVICES=192.168.12.94:11800
    export NETRAA_AGENT_PROTOCOL=grpc
    export NETRAA_AGENT_NAME=py_b
    netraa-python run python3 service_b.py --port 5002`} />
    
    <p className="font-semibold text-sm mt-4 mb-2">Microservice C</p>
    
    <CodeBlock code={`export NETRAA_AGENT_COLLECTOR_BACKEND_SERVICES=192.168.12.94:11800
    export NETRAA_AGENT_PROTOCOL=grpc
    export NETRAA_AGENT_NAME=py_c
    netraa-python run python3 service_c.py --port 5003`} />
    
    </section>
    
    {/* Recommended Config */}
    <section className="mb-10">
    <h2 className="text-2xl font-bold text-text-main dark:text-white mb-4">
    Recommended Configuration
    </h2>
    
    <ul className="list-disc pl-6 text-sm text-text-muted space-y-2">
    <li>Always define <strong>NETRAA_AGENT_NAME</strong> explicitly.</li>
    <li>Use meaningful service names aligned with business logic.</li>
    <li>Ensure all internal services are instrumented to avoid partial traces.</li>
    <li>Run each service in an isolated environment or terminal.</li>
    </ul>
    
    </section>
    {/* Visualize Data CTA */}
    <section className="mb-10 flex justify-center">
      <button
        onClick={() => navigate("/homeservices")}
        className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-bold text-white
                   bg-gradient-to-r from-orange-500 to-red-600
                   hover:from-orange-600 hover:to-red-700
                   shadow-md hover:shadow-lg transition-all duration-300
                   dark:shadow-orange-900/30"
      >
        Visualize Data
        <FiChevronRight className="w-4 h-4" />
      </button>
    </section>
    
    
    </div>
    </main>
    </div>
    );
    }